import type { AssistantContextRef } from '~/data/api/client'

import type { BillingComposerEntity, BillingComposerMode, BillingDraftFields } from './types'

/** Built-in assistant skill that enables the billing tools + billing prompt for a turn. */
export const BILLING_COMPOSER_SKILL_ID = 'builtin:billing-composer'

/** The contract caps the `billing_draft` context ref at roughly 12 KB of compact JSON. */
export const DRAFT_REF_MAX_CHARS = 12 * 1024
/** Time-of-use periods beyond this count are dropped from the snapshot with a `_truncated` marker. */
export const DRAFT_REF_MAX_TOU_PERIODS = 40

/** Label the backend expects on the current-form ref. */
const DRAFT_REF_LABEL = 'Current draft'

export type ComposerContextInput = {
  entity?: BillingComposerEntity
  mode: BillingComposerMode
  entityId?: string
  entityName?: string
  /** Current form state already converted to the draft-fields (API request) shape. */
  formSnapshot?: BillingDraftFields
  pathname: string
  pageTitle: string
}

/**
 * Builds the context refs for one composer turn per the shared contract:
 * the focused product/agreement (so the backend prefetches it), the current unsaved form as a
 * `billing_draft` ref carrying compact JSON, and the page.
 */
export function buildComposerContextRefs(input: ComposerContextInput): AssistantContextRef[] {
  const refs: AssistantContextRef[] = []

  if (input.entity && input.entityId) {
    const name = input.entityName?.trim() || input.entityId
    refs.push({ type: input.entity, id: input.entityId, label: name, name })
  }

  if (input.entity && input.formSnapshot && input.mode !== 'view') {
    refs.push({
      type: 'billing_draft',
      id: input.entity,
      label: DRAFT_REF_LABEL,
      name: serializeDraftSnapshot(input.formSnapshot)
    })
  }

  refs.push({ type: 'page', id: input.pathname, label: input.pageTitle })
  return refs
}

/**
 * Compact JSON of the form snapshot, trimmed to the contract's size budget. Trimming is progressive so
 * the assistant always sees the fields it needs most: TOU periods past 40 go first, then per-asset
 * selections collapse to a count, then extra rates, and as a last resort only scalar fields remain.
 */
export function serializeDraftSnapshot(snapshot: BillingDraftFields): string {
  let working: Record<string, unknown> = truncateTouPeriods(snapshot as Record<string, unknown>)
  let json = JSON.stringify(working)
  if (json.length <= DRAFT_REF_MAX_CHARS) return json

  working = collapseEntitySelections(working)
  json = JSON.stringify(working)
  if (json.length <= DRAFT_REF_MAX_CHARS) return json

  working = keepFirstRate(working)
  json = JSON.stringify(working)
  if (json.length <= DRAFT_REF_MAX_CHARS) return json

  return JSON.stringify(scalarsOnly(working))
}

function truncateTouPeriods(snapshot: Record<string, unknown>): Record<string, unknown> {
  const tou = snapshot.timeOfUsePricing
  if (!isRecord(tou) || !Array.isArray(tou.rates)) return snapshot

  let budget = DRAFT_REF_MAX_TOU_PERIODS
  let truncated = false
  const rates = tou.rates.map((rate) => {
    if (!isRecord(rate) || !Array.isArray(rate.periods)) return rate
    const keep = Math.max(0, Math.min(rate.periods.length, budget))
    budget -= keep
    if (keep < rate.periods.length) {
      truncated = true
      return { ...rate, periods: rate.periods.slice(0, keep), _truncated: true }
    }
    return rate
  })
  if (!truncated) return snapshot
  return { ...snapshot, timeOfUsePricing: { ...tou, rates, _truncated: true } }
}

function collapseEntitySelections(snapshot: Record<string, unknown>): Record<string, unknown> {
  if (!Array.isArray(snapshot.entitySelections)) return snapshot
  return {
    ...snapshot,
    entitySelections: undefined,
    entitySelectionCount: snapshot.entitySelections.length,
    _truncated: true
  }
}

function keepFirstRate(snapshot: Record<string, unknown>): Record<string, unknown> {
  const tou = snapshot.timeOfUsePricing
  if (!isRecord(tou) || !Array.isArray(tou.rates) || tou.rates.length <= 1) return snapshot
  return {
    ...snapshot,
    timeOfUsePricing: { ...tou, rates: tou.rates.slice(0, 1), rateCount: tou.rates.length, _truncated: true },
    _truncated: true
  }
}

function scalarsOnly(snapshot: Record<string, unknown>): Record<string, unknown> {
  const scalars = Object.fromEntries(
    Object.entries(snapshot).filter(([, value]) => value === null || ['string', 'number', 'boolean'].includes(typeof value))
  )
  return { ...scalars, _truncated: true }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}
